import React, { useState } from 'react';
import { History } from 'lucide-react';
import {
  useSubcategories,
  useCreateSubcategory,
  useUpdateSubcategory,
  useDeleteSubcategory,
} from '../../hooks/useCategories';
import SubcategoryForm from './SubcategoryForm';
import AuditDrawer from '../audit/AuditDrawer';
import type { Subcategory, SubcategoryCreate, SubcategoryUpdate } from '../../types';

interface SubcategoryManagerProps {
  categoryId: string;
}

const SubcategoryManager: React.FC<SubcategoryManagerProps> = ({ categoryId }) => {
  const { data: subcategories, isLoading, error } = useSubcategories(categoryId);
  const createSubcategory = useCreateSubcategory();
  const updateSubcategory = useUpdateSubcategory();
  const deleteSubcategory = useDeleteSubcategory();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState<Subcategory | null>(null);
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    subcategoryId: string;
    subcategoryName: string;
  }>({
    isOpen: false,
    subcategoryId: '',
    subcategoryName: '',
  });

  const handleCreate = async (data: SubcategoryCreate | SubcategoryUpdate) => {
    try {
      await createSubcategory.mutateAsync(data as SubcategoryCreate);
      setShowCreateForm(false);
    } catch (error) {
      console.error('Error creating subcategory:', error);
    }
  };

  const handleUpdate = async (data: SubcategoryCreate | SubcategoryUpdate) => {
    if (!editingSubcategory) return;

    try {
      await updateSubcategory.mutateAsync({
        subcategoryId: editingSubcategory.subcategoryId,
        data: data as SubcategoryUpdate,
      });
      setEditingSubcategory(null);
    } catch (error) {
      console.error('Error updating subcategory:', error);
    }
  };

  const handleDelete = async (subcategory: Subcategory) => {
    setDeletingSubcategory(subcategory);
  };

  const confirmDelete = async () => {
    if (!deletingSubcategory) return;

    try {
      await deleteSubcategory.mutateAsync({
        subcategoryId: deletingSubcategory.subcategoryId,
        categoryId,
      });
      setDeletingSubcategory(null);
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      setDeletingSubcategory(null);
    }
  };

  const handleViewHistory = (subcategory: Subcategory) => {
    setAuditDrawer({
      isOpen: true,
      subcategoryId: subcategory.subcategoryId,
      subcategoryName: subcategory.name,
    });
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      subcategoryId: '',
      subcategoryName: '',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
        <p className="text-sm text-red-800">
          Error al cargar subcategorías: {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
      </div>
    );
  }

  const activeSubcategories = subcategories?.filter((sub: Subcategory) => sub.isActive) || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-semibold text-gray-700">Subcategorías</h4>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          + Agregar
        </button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h5 className="text-sm font-semibold text-gray-900 mb-3">Nueva Subcategoría</h5>
          <SubcategoryForm
            categoryId={categoryId}
            onSubmit={handleCreate}
            onCancel={() => setShowCreateForm(false)}
            isLoading={createSubcategory.isLoading}
          />
        </div>
      )}

      {/* Edit Form */}
      {editingSubcategory && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h5 className="text-sm font-semibold text-gray-900 mb-3">Editar Subcategoría</h5>
          <SubcategoryForm
            categoryId={categoryId}
            subcategory={editingSubcategory}
            onSubmit={handleUpdate}
            onCancel={() => setEditingSubcategory(null)}
            isLoading={updateSubcategory.isLoading}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingSubcategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar Eliminación</h3>
            <p className="text-gray-600 mb-6">
              ¿Está seguro que desea eliminar la subcategoría "{deletingSubcategory.name}"? Esta
              acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingSubcategory(null)}
                disabled={deleteSubcategory.isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteSubcategory.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteSubcategory.isLoading ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subcategories List */}
      {activeSubcategories.length === 0 ? (
        <div className="text-center py-4 text-sm text-gray-500">
          No hay subcategorías. Agrega la primera subcategoría.
        </div>
      ) : (
        <div className="space-y-2">
          {activeSubcategories.map((subcategory: Subcategory) => (
            <div
              key={subcategory.subcategoryId}
              className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 hover:border-gray-300"
            >
              <div className="flex-1">
                <h5 className="text-sm font-medium text-gray-900">{subcategory.name}</h5>
                {subcategory.description && (
                  <p className="text-xs text-gray-600 mt-1">{subcategory.description}</p>
                )}
              </div>
              <div className="flex items-center space-x-1 ml-4">
                <button
                  onClick={() => handleViewHistory(subcategory)}
                  className="p-1.5 text-gray-600 hover:bg-gray-50 rounded focus:outline-none focus:ring-2 focus:ring-gray-500"
                  title="Ver historial"
                >
                  <History className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingSubcategory(subcategory)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Editar subcategoría"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(subcategory)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Eliminar subcategoría"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="SUBCATEGORY"
        entityId={auditDrawer.subcategoryId}
        entityName={auditDrawer.subcategoryName}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </div>
  );
};

export default SubcategoryManager;
